<template>
  <!-- ====================== -->
  <!-- TAB : QUANTITY         -->
  <!-- (Artefact / DM view)   -->
  <!-- ====================== -->
  <div>
    <div class="row mt-2 mb-2"></div>
    <p class="bb-hdg mt-2 mb-3">Quantity Details</p>
    <div class="row mt-2 mb-2">
      <template v-if="activeItem.tallyable">
        <div class="col-5">
          <CounterEmit
            ref="tallyQtyCounter"
            :pass_ref="'tally_qty'"
            :pass_label="'Tally Qty'"
            :pass_count="resolvedTallyQty"
            :pass_min="tallyQtyMin"
            :pass_max="99"
            :pass_digits="2"
            @change="onCounterChanged"
          />
        </div>
      </template>
      <div
        :class="activeItem.tallyable ? 'col-3 form-group' : 'col-3 form-group'"
      >
        <div class="form-floating">
          <input
            type="number"
            step="0.1"
            class="form-control text-center no-spinner"
            :value="encInputValue"
            id="enc"
            @input="onEncInput"
            @blur="onEncFieldBlur"
            @click="clearInputError('enc')"
            :class="{ 'is-invalid': errorFields.includes('enc') }"
          />
          <label
            class="bb-float-label"
            for="enc"
            >Enc</label
          >
        </div>
      </div>
      <template v-if="activeItem.tallyable">
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="formatEnc(calcQtyTotEnc)"
              id="qty_tot_enc"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="qty_tot_enc"
              >Total Enc</label
            >
          </div>
        </div>
      </template>
    </div>
    <div class="row mt-3 mb-2"></div>
    <div v-if="activeItem.countable">
      <p class="bb-sub-hdg mb-3 mt-2">Unit Details</p>
      <!-- unit name — always editable for DM -->
      <div class="row mt-2 mb-2">
        <div class="col-5 form-group">
          <div class="form-floating">
            <v-select
              :model-value="localItem.unit_name ?? activeItem.unit_name"
              :options="units"
              class="form-control"
              placeholder="Select..."
              :class="{ 'is-invalid': errorFields.includes('unit_name') }"
              @keydown="onKeyDown($event)"
              @update:modelValue="onUnitSelect"
              id="unit_name"
              @click="clearInputError('unit_name')"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="unit_name"
              >Unit Name</label
            >
          </div>
        </div>
      </div>
      <!-- max unit count — always editable CounterEmit for DM -->
      <div
        v-if="activeItem.unit_name || localItem.unit_name"
        class="row mt-2 mb-2"
      >
        <div class="col-5">
          <CounterEmit
            ref="maxUnitCounter"
            :pass_ref="'max_unit_count'"
            :pass_label="`Max ${localItem.unit_name ?? activeItem.unit_name ?? ''}s`"
            :pass_count="
              localItem.max_unit_count ?? activeItem.max_unit_count ?? 1
            "
            :pass_min="1"
            :pass_max="99"
            :pass_digits="2"
            :invalid="errorFields.includes('max_unit_count')"
            @change="onCounterChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ArtefactQuantityTab',
  components: {
    CounterEmit,
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    units: {
      type: Array,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    formatEnc: {
      type: Function,
      required: true,
    },
    calcQtyTotEnc: {
      type: Number,
      required: true,
    },
    onCounterChanged: {
      type: Function,
      required: true,
    },
    clearInputError: {
      type: Function,
      required: true,
    },
    onEncInput: {
      type: Function,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    onUnitSelect: {
      type: Function,
      required: true,
    },
    isNewItem: {
      type: Boolean,
      required: true,
    },
    forceEncTwoDp: {
      type: Boolean,
      default: false,
    },
    onEncBlur: {
      type: Function,
      default: null,
    },
    tallyQtyMin: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    encInputValue() {
      const enc = this.activeItem?.enc

      if (!this.forceEncTwoDp) {
        return enc
      }

      if (enc === null || enc === undefined || enc === '') {
        return ''
      }

      return this.formatEnc(enc)
    },
    resolvedTallyQty() {
      const localValue = this.localItem?.tally_qty
      if (
        localValue !== null &&
        localValue !== undefined &&
        localValue !== ''
      ) {
        return Number(localValue)
      }

      const activeValue = this.activeItem?.tally_qty
      if (
        activeValue !== null &&
        activeValue !== undefined &&
        activeValue !== ''
      ) {
        return Number(activeValue)
      }

      return this.tallyQtyMin
    },
  },
  methods: {
    onEncFieldBlur(event) {
      if (!this.forceEncTwoDp || !event?.target) return

      const raw = event.target.value ?? ''
      const parsed = Number(raw)
      if (raw === '' || Number.isNaN(parsed)) return

      const fixed = parsed.toFixed(2)
      event.target.value = fixed

      if (typeof this.onEncBlur === 'function') {
        this.onEncBlur(fixed)
      }
    },
  },
}
</script>
